import { FC, useState } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Button, Col, Collapse, Divider, Row, Typography } from 'antd';
import { Shapes } from 'lucide-react';
import { useQueryState } from 'nuqs';
import { TbArrowLeft, TbArrowRight, TbArrowsDiff, TbChevronRight, TbInfoCircle } from 'react-icons/tb';
import { Link } from 'react-router-dom';

import { FaviconImage } from '@/assets';
import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { consentQuery } from '@/queries';
import { ButtonSkeleton, PlatformAlert, PublicConsentCard } from '@/shared';
import { OAuthConsentRequest } from '@/shared/oauth/scopes';
import { useUserStore } from '@/stores';
import { imgLogoStyling } from '@/theme';
import { corporateLogo } from '@/utilities';

import { useOAuthConsentCollapsedItems } from '../../hooks';
import OwnerAdminWorkspaceSelectionDropdown from '../OwnerAdminWorkspaceSelectionDropdown';

interface ConsentCardProps {
    request: OAuthConsentRequest;
    isPreview?: boolean;
}

const ConsentCard: FC<ConsentCardProps> = ({ request, isPreview = false }): JSX.Element => {
    const [loading, setLoading] = useState(false);

    const [requestId] = useQueryState('id', consentQuery);

    const { token } = useAntDesignTheme();

    const { selectedUser: user } = useUserStore();

    const isLoggedIn = !!user?.id;

    const { items } = useOAuthConsentCollapsedItems(request);

    const approve = async () => {
        try {
            setLoading(true);
            await authorizedAxiosInstance.post(`/api/oauth-applications/consent/${requestId}/approve`);
        } catch (error) {
            captureException(error);
        } finally {
            setLoading(false);
        }
    };

    const deny = async () => {
        try {
            setLoading(true);
            await authorizedAxiosInstance.post(`/api/oauth-applications/consent/${requestId}/deny`);
        } catch (error) {
            captureException(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <PublicConsentCard>
            <div style={{ width: '100%', textAlign: 'center' }}>
                <Row align='middle' style={{ marginBottom: '1rem' }} gutter={[8, 0]}>
                    <Col flex='auto'></Col>
                    <Col flex='none'>
                        <Avatar
                            shape='square'
                            size='large'
                            style={{
                                border: `1px solid ${token.colorBorder}`,
                                backgroundColor: token.colorBgContainer,
                            }}
                            icon={<Shapes size='1.5rem' style={{ color: token.colorTextTertiary }} />}
                            src={
                                request?.client?.iconUrl ||
                                (request?.client?.websiteUrl && corporateLogo(request?.client?.websiteUrl))
                            }
                        />
                    </Col>
                    <Col flex='none'>
                        <TbArrowsDiff
                            size='1.25rem'
                            style={{ verticalAlign: '-0.35rem', color: token.colorTextTertiary }}
                        />
                    </Col>
                    <Col flex='none'>
                        <Avatar
                            shape='square'
                            size='large'
                            style={{
                                border: `1px solid ${token.colorBorder}`,
                                backgroundColor: '#ffffff',
                            }}
                            icon={
                                <img
                                    src={FaviconImage}
                                    style={{ ...imgLogoStyling, transform: 'scale(0.85)' }}
                                    alt='Countercyclical Integration Image'
                                />
                            }
                        />
                    </Col>
                    <Col flex='auto' />
                </Row>
                {request?.client?.name ? (
                    <Typography.Title level={5}>Connect '{request?.client?.name}'</Typography.Title>
                ) : (
                    <div>
                        <ButtonSkeleton style={{ width: 16 * 18, marginBottom: '1rem' }} />
                    </div>
                )}
                <Typography.Text type='secondary'>
                    This application is requesting permission to integrate with the following workspace in
                    Countercyclical:
                </Typography.Text>
            </div>
            <Divider style={{ margin: '1rem 0', marginBottom: '0.5rem' }} />
            {isLoggedIn ? (
                <>
                    <div style={{ marginBottom: '0.5rem' }}>
                        <small style={{ color: token.colorTextTertiary }}>Workspace</small>
                    </div>
                    {request?.client ? (
                        <OwnerAdminWorkspaceSelectionDropdown isPreview={isPreview} />
                    ) : (
                        <div>
                            <ButtonSkeleton block />
                        </div>
                    )}
                </>
            ) : (
                <PlatformAlert
                    status='warning'
                    title='Authentication required'
                    description='Please log in to select your workspace & complete integrationing with this external service.'
                    styles={{ marginTop: '1rem' }}
                />
            )}
            <Divider style={{ margin: '1rem 0' }} />
            {request?.client ? (
                <Collapse
                    accordion
                    items={items}
                    expandIcon={(props) => (
                        <TbChevronRight
                            style={{
                                transform: `rotate(${props.isActive ? '90deg' : '0deg'})`,
                                color: token.colorTextTertiary,
                            }}
                        />
                    )}
                    style={{ borderRadius: token.borderRadiusLG * 2, margin: '1rem 0', overflow: 'auto' }}
                />
            ) : (
                <div>
                    <div>
                        <ButtonSkeleton block size='large' />
                    </div>
                    <div style={{ margin: '0.5rem 0' }}>
                        <ButtonSkeleton block size='large' />
                    </div>
                    <div>
                        <ButtonSkeleton block size='large' />
                    </div>
                </div>
            )}
            <Divider style={{ margin: '1rem 0' }} />
            <Row gutter={[16, 0]}>
                <Col flex='auto'>
                    {request?.client ? (
                        <Button onClick={deny} block loading={loading} disabled={!isLoggedIn || isPreview || loading}>
                            <TbArrowLeft size='1rem' />
                            Cancel
                        </Button>
                    ) : (
                        <div>
                            <ButtonSkeleton block />
                        </div>
                    )}
                </Col>
                <Col flex='auto'>
                    {request?.client ? (
                        <Button
                            type='primary'
                            onClick={approve}
                            block
                            loading={loading}
                            disabled={!isLoggedIn || isPreview || loading}
                        >
                            Continue
                            <TbArrowRight size='1rem' />
                        </Button>
                    ) : (
                        <div>
                            <ButtonSkeleton block />
                        </div>
                    )}
                </Col>
            </Row>
            <Divider style={{ margin: '1.25rem 0', marginBottom: '1rem' }} />
            <Row>
                <div>
                    <small style={{ color: token.colorTextTertiary }}>
                        <TbInfoCircle style={{ marginRight: '0.25rem', verticalAlign: '-0.125rem' }} />
                        By selecting 'Continue', you agree to Countercyclical's{' '}
                        <a href='https://countercyclical.com/tos'>Terms of Service</a> &{' '}
                        <a href='https://countercyclical.com/privacy'>Privacy Policy</a>. Make sure you trust the
                        developer of this integration & look over the scope before proceeding. You can always remove
                        application integrations in your{' '}
                        <Link to='/settings'>
                            Settings
                            <TbArrowRight style={{ verticalAlign: '-0.125rem', marginLeft: '0.125rem' }} />
                        </Link>
                    </small>
                </div>
            </Row>
        </PublicConsentCard>
    );
};

export default ConsentCard;
